/**
 * Workflow Builder (docs/features/workflows.md), like Slack's: a workflow is a trigger and a list of steps. Managers
 * edit a working copy (`definition`) and publish it; runs use the published version (frozen per run). Steps pass
 * outputs to later steps as variables (`{{trigger.user}}`, `{{steps.s2.answer}}`) that go into text and conditions.
 *
 * - **Triggers:** a link / shortcut in a channel (its workflows menu and the "/" menu), someone joining a channel, an
 *   emoji reaction on a message in a channel, a schedule (once or repeating, in a time zone) and a webhook with
 *   declared variables (`POST /v1/workflow-hooks/:ws/:id/:secret`).
 * - **Steps:** send a message (a channel, a person, or the person who ran it), a form (a Block Kit modal for the person
 *   who ran it; link triggers only, like Slack), add a reaction, create a Docs page or database row, add someone to a
 *   channel, wait, and an app's step (a signed `function_executed` event; the app answers with outputs or calls
 *   `functions.completeSuccess` / `functions.completeError`). Any step can have a condition (runs only when it holds).
 * - **Who manages:** members (not guests) create workflows unless owners/admins limit it to themselves; the creator,
 *   the workflow's managers and owners/admins edit, publish, unpublish and see the activity log.
 */
import { z } from "zod";
import { Id, Timestamp } from "./entities.ts";

/** Workflow ids start with this (form modals use the workflow id where app modals use the app id). */
export const WORKFLOW_ID_PREFIX = "Wf";
export const MAX_WORKFLOW_STEPS = 30;
export const MAX_FORM_FIELDS = 10;
/** Longest wait step: 30 days. */
export const MAX_DELAY_MINUTES = 30 * 24 * 60;
/** Workflows per workspace. */
export const MAX_WORKFLOWS = 200;
/** Runs kept in each workflow's activity log. */
export const WORKFLOW_RUNS_KEPT = 100;

/** What a variable holds: user / channel / message / page / row are ids, date is YYYY-MM-DD, time an ISO instant. */
export const WorkflowVarType = z.enum(["text", "number", "user", "channel", "message", "date", "time", "page", "row"]);
export type WorkflowVarType = z.infer<typeof WorkflowVarType>;

/** A variable's value at run time. */
export const WorkflowValue = z.object({ type: WorkflowVarType, value: z.string().max(4000) });
export type WorkflowValue = z.infer<typeof WorkflowValue>;

export const StepId = z.string().regex(/^[a-z][a-z0-9]{0,15}$/);
export const VariableKey = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/);

/** A repeat rule: once, every day, weekdays (Mon–Fri), every week on `days` (0 = Sunday), every month on the start's day. */
export const ScheduleRepeat = z.enum(["none", "daily", "weekdays", "weekly", "monthly"]);
export type ScheduleRepeat = z.infer<typeof ScheduleRepeat>;

export const WorkflowSchedule = z.object({
  /** The first run (its wall-clock time in `timeZone` is the time of every repeat). */
  startAt: Timestamp,
  /** IANA zone, e.g. "America/Sao_Paulo". */
  timeZone: z.string().min(1).max(64),
  repeat: ScheduleRepeat,
  /** Weekly: the weekdays (0 = Sunday … 6); default the start's weekday. */
  days: z.array(z.number().int().min(0).max(6)).max(7).optional(),
});
export type WorkflowSchedule = z.infer<typeof WorkflowSchedule>;

export const WebhookVariable = z.object({ key: VariableKey, type: z.enum(["text", "user", "channel"]) });
export type WebhookVariable = z.infer<typeof WebhookVariable>;

export const WorkflowTrigger = z.discriminatedUnion("type", [
  /** A link and shortcut: the channel's workflows menu and its "/" menu. */
  z.object({ type: z.literal("link"), channelId: Id }),
  /** Someone joins the channel. */
  z.object({ type: z.literal("member_joined"), channelId: Id }),
  /** Someone reacts with `emoji` (the character, or a custom emoji's `:name:`) to a message in the channel. */
  z.object({ type: z.literal("reaction"), channelId: Id, emoji: z.string().min(1).max(64) }),
  z.object({ type: z.literal("schedule"), schedule: WorkflowSchedule }),
  /** A POST to the workflow's secret URL with these variables as JSON keys. */
  z.object({ type: z.literal("webhook"), variables: z.array(WebhookVariable).max(20) }),
]);
export type WorkflowTrigger = z.infer<typeof WorkflowTrigger>;
export type WorkflowTriggerType = WorkflowTrigger["type"];

export const ConditionOp = z.enum(["is", "is_not", "contains", "is_empty", "is_not_empty", "greater_than", "less_than"]);
export type ConditionOp = z.infer<typeof ConditionOp>;

/** `value` (a template, usually one variable) compared with `compare` (a template; ignored by the empty checks). */
export const WorkflowCondition = z.object({ value: z.string().min(1).max(500), op: ConditionOp, compare: z.string().max(500).optional() });
export type WorkflowCondition = z.infer<typeof WorkflowCondition>;

/** Who a message goes to: a channel, a person, the person who ran the workflow, or a user / channel variable. */
export const WorkflowRecipient = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("channel"), channelId: Id }),
  z.object({ kind: z.literal("user"), userId: Id }),
  z.object({ kind: z.literal("runner") }),
  /** A variable reference such as "steps.s1.reviewer" (a user or channel). */
  z.object({ kind: z.literal("variable"), ref: z.string().min(1).max(100) }),
]);
export type WorkflowRecipient = z.infer<typeof WorkflowRecipient>;

export const FormFieldKind = z.enum(["text", "paragraph", "number", "select", "user", "channel", "date"]);
export type FormFieldKind = z.infer<typeof FormFieldKind>;

export const FormField = z.object({
  /** The output name (`{{steps.<step>.<id>}}`). */
  id: VariableKey,
  label: z.string().min(1).max(100),
  kind: FormFieldKind,
  required: z.boolean(),
  /** select: the choices (1–20). */
  options: z.array(z.string().min(1).max(75)).max(20).optional(),
});
export type FormField = z.infer<typeof FormField>;

export const AppStepOutput = z.object({ name: VariableKey, type: WorkflowVarType });
export type AppStepOutput = z.infer<typeof AppStepOutput>;

export const WorkflowStep = z.discriminatedUnion("type", [
  z.object({ type: z.literal("send_message"), id: StepId, condition: WorkflowCondition.optional(), to: WorkflowRecipient, text: z.string().min(1).max(4000) }),
  z.object({
    type: z.literal("form"), id: StepId, condition: WorkflowCondition.optional(),
    /** The modal's title (Slack: 24 characters). */
    title: z.string().min(1).max(24),
    submitLabel: z.string().max(24).optional(),
    fields: z.array(FormField).min(1).max(MAX_FORM_FIELDS),
  }),
  /** `message`: a message variable ("{{trigger.message}}", "{{steps.s1.message}}"). */
  z.object({ type: z.literal("add_reaction"), id: StepId, condition: WorkflowCondition.optional(), message: z.string().min(1).max(100), emoji: z.string().min(1).max(64) }),
  z.object({ type: z.literal("create_page"), id: StepId, condition: WorkflowCondition.optional(), title: z.string().min(1).max(500), text: z.string().max(4000).optional() }),
  /** A row titled `title`; `values` fill text properties by property id (templates). */
  z.object({ type: z.literal("create_row"), id: StepId, condition: WorkflowCondition.optional(), databaseId: Id, title: z.string().min(1).max(500), values: z.record(z.string(), z.string().max(2000)).optional() }),
  /** `user`: a user variable or id. */
  z.object({ type: z.literal("add_to_channel"), id: StepId, condition: WorkflowCondition.optional(), channelId: Id, user: z.string().min(1).max(100) }),
  z.object({ type: z.literal("delay"), id: StepId, condition: WorkflowCondition.optional(), minutes: z.number().int().min(1).max(MAX_DELAY_MINUTES) }),
  /** An app's step (Slack's custom function): `inputs` are templates; the app answers with `outputs`. */
  z.object({
    type: z.literal("app_step"), id: StepId, condition: WorkflowCondition.optional(),
    appId: Id,
    callbackId: z.string().min(1).max(64),
    title: z.string().min(1).max(80),
    inputs: z.record(z.string(), z.string().max(2000)),
    outputs: z.array(AppStepOutput).max(20),
  }),
]);
export type WorkflowStep = z.infer<typeof WorkflowStep>;
export type WorkflowStepType = WorkflowStep["type"];

export const WorkflowDefinition = z.object({
  name: z.string().min(1).max(80),
  description: z.string().max(200).optional(),
  /** An emoji shown next to the name. */
  icon: z.string().max(16).optional(),
  trigger: WorkflowTrigger,
  steps: z.array(WorkflowStep).max(MAX_WORKFLOW_STEPS),
});
export type WorkflowDefinition = z.infer<typeof WorkflowDefinition>;

export const Workflow = z.object({
  id: Id,
  /** Managers: the working copy. Others: the published version. */
  definition: WorkflowDefinition,
  published: z.boolean(),
  /** Managers: the working copy has changes that aren't published yet. */
  changed: z.boolean().optional(),
  createdBy: Id,
  /** People besides the creator who can manage it (Slack's "workflow managers"). */
  managers: z.array(Id),
  /** Bumped by every publish. */
  version: z.number().int().nonnegative(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  publishedAt: Timestamp.optional(),
  /** Can I edit, publish and see the activity log? */
  canManage: z.boolean(),
  /** Schedule triggers: the next run. */
  nextRunAt: Timestamp.optional(),
  /** Webhook triggers: a secret URL exists (shown once when made). */
  hasWebhook: z.boolean().optional(),
});
export type Workflow = z.infer<typeof Workflow>;

/** Who can create workflows (Slack: workspace settings → Workflow Builder). */
export const WorkflowCreators = z.enum(["everyone", "admins"]);
export type WorkflowCreators = z.infer<typeof WorkflowCreators>;

/** GET /v1/w/:ws/workflows — those I manage plus published link workflows in channels I'm in. */
export const ListWorkflowsResponse = z.object({ workflows: z.array(Workflow), canCreate: z.boolean(), creators: WorkflowCreators });
export type ListWorkflowsResponse = z.infer<typeof ListWorkflowsResponse>;

export const CreateWorkflowRequest = z.object({ definition: WorkflowDefinition, managers: z.array(Id).max(20).optional() });
export type CreateWorkflowRequest = z.infer<typeof CreateWorkflowRequest>;
export const UpdateWorkflowRequest = z.object({ definition: WorkflowDefinition.optional(), managers: z.array(Id).max(20).optional() });
export type UpdateWorkflowRequest = z.infer<typeof UpdateWorkflowRequest>;
export const WorkflowResponse = z.object({ workflow: Workflow, /** Webhook triggers: the secret URL, only right after it was made. */ webhookUrl: z.string().optional() });
export type WorkflowResponse = z.infer<typeof WorkflowResponse>;

export const UpdateWorkflowSettingsRequest = z.object({ creators: WorkflowCreators });
export type UpdateWorkflowSettingsRequest = z.infer<typeof UpdateWorkflowSettingsRequest>;
export const WorkflowSettingsResponse = z.object({ creators: WorkflowCreators });
export type WorkflowSettingsResponse = z.infer<typeof WorkflowSettingsResponse>;

/**
 * POST /v1/w/:ws/workflows/:id/run — run a link workflow (from the channel it lives in). `clientToken` makes its form
 * open on this client (AppModalViewModel). `test`: managers run the working copy from the builder ("Test run").
 */
export const RunWorkflowRequest = z.object({ channelId: Id.optional(), clientToken: z.string().max(64).optional(), test: z.boolean().optional() });
export type RunWorkflowRequest = z.infer<typeof RunWorkflowRequest>;

export const WorkflowRunStatus = z.enum(["running", "waiting", "completed", "failed", "cancelled"]);
export type WorkflowRunStatus = z.infer<typeof WorkflowRunStatus>;
export const WorkflowStepStatus = z.enum(["running", "waiting", "done", "skipped", "failed"]);
export type WorkflowStepStatus = z.infer<typeof WorkflowStepStatus>;

export const WorkflowStepResult = z.object({
  stepId: StepId,
  status: WorkflowStepStatus,
  startedAt: Timestamp,
  endedAt: Timestamp.optional(),
  /** Tries so far (retries are safe: a step's effect happens once). */
  attempts: z.number().int().nonnegative(),
  error: z.string().optional(),
  outputs: z.record(z.string(), WorkflowValue).optional(),
});
export type WorkflowStepResult = z.infer<typeof WorkflowStepResult>;

/** One run in the activity log. */
export const WorkflowRun = z.object({
  id: Id,
  workflowId: Id,
  version: z.number().int().nonnegative(),
  status: WorkflowRunStatus,
  trigger: z.enum(["link", "member_joined", "reaction", "schedule", "webhook"]),
  test: z.boolean().optional(),
  /** The person who ran it (link), joined, or reacted. */
  userId: Id.optional(),
  startedAt: Timestamp,
  endedAt: Timestamp.optional(),
  error: z.string().optional(),
  steps: z.array(WorkflowStepResult),
});
export type WorkflowRun = z.infer<typeof WorkflowRun>;

export const WorkflowRunResponse = z.object({ run: WorkflowRun });
export type WorkflowRunResponse = z.infer<typeof WorkflowRunResponse>;
export const ListWorkflowRunsResponse = z.object({ runs: z.array(WorkflowRun) });
export type ListWorkflowRunsResponse = z.infer<typeof ListWorkflowRunsResponse>;

/** The webhook trigger's answer. */
export const WorkflowHookResponse = z.object({ ok: z.boolean(), runId: Id });
export type WorkflowHookResponse = z.infer<typeof WorkflowHookResponse>;
